import React, { useState, useEffect, useCallback, memo } from 'react';
import { TextField, IconButton, Divider } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import Iconify from '../../../components/iconify';
import DeletePopup from '../../../commons/DeletePopup';




const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.columnHeader}`]: {
        backgroundColor: 'white',
        color: 'black',
        fontWeight: 'bold',
        fontSize: '13px',
        lineHeight: '40px',  // Keep line height consistent
        paddingLeft: '5px', // Remove left padding
    },

    '& .MuiDataGrid-cell': {
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`, // Top border for all cells
        fontSize: '13px', // Adjust font size if needed
        padding: '4px 5px', // Adjust padding to remove vertical space
        display: 'flex', // Ensure the content is aligned top
        alignItems: 'flex-start', // Align content at the top
        whiteSpace: 'normal', // Allow text to wrap
        wordWrap: 'break-word', // Ensure long words break and wrap to the next line
        overflow: 'visible',  // Make sure overflow content is visible
        height: 'auto', // Allow the cell height to expand based on content
        '&:hover': {
            backgroundColor: 'white',
        },
    },

    // Remove top border for first row
    '& .MuiDataGrid-row:first-child .MuiDataGrid-cell': {
        borderTop: 'none',  // No top border for the first row
    },

    '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,  // Bottom border for last row
    },

    '& .MuiDataGrid-row': {
        minHeight: '45px',  // Ensure no extra space vertically for rows
        display: 'flex', // Align rows as flex containers
        alignItems: 'stretch', // Stretch row content to match height
        '&:hover': {
            backgroundColor: 'white',
        },
        padding: '0px 0px'

    },

    '& .MuiDataGrid-columnSeparator': {
        display: 'none',
    },

    '& .MuiDataGrid-menuIcon': {
        display: 'none',
    },


}));


const InternalGrid = memo(({ rows, onEditCell, onDeleteRow, onDeleteAllRows, showErrorMessage }) => {
    // Define columns with textboxes and delete button
    const columns = [
        {
            field: 'Item',
            headerName: 'Brand',
            flex: 1,
            renderCell: (params) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word", display: 'flex', alignItems: 'center', height: '100%' }}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'MT',
            headerName: 'MT',
            flex: 0.5,
            renderHeader: () => (
                <span style={{ paddingLeft: '5px', fontSize: '13px' }}>
                    MT
                    <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                </span>
            ),
            renderCell: (params) => (
                <TextField
                    fullWidth
                    placeholder='MT Value *'
                    variant="outlined"
                    size="small"
                    type="number"
                    defaultValue={params.row.MT || ""}
                    helperText={showErrorMessage && !params.row.MT ? <span style={{ color: 'red', fontSize: '10px' }}>MT value is required</span> : ""}
                    FormHelperTextProps={{ sx: { color: "red", fontSize: '10px' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            padding: '2px',
                            fontSize: '12px',
                            height: '30px',
                            width:'110px'
                        },
                        '& .MuiInputLabel-root': {
                            fontSize: '12px',
                        },
                        '& .MuiFormHelperText-root': {
                            fontSize: '10px'
                        }
                    }}
                    onBlur={(event) => {
                        if (!event.target.value.trim()) {
                            params.row.MT = "";
                        } else {
                            onEditCell(event, params.row.id, "MT");
                        }
                    }}
                />
            ),
        },
        {
            field: 'Point',
            headerName: 'Point',
            flex: 0.5,
            renderHeader: () => (
                <span style={{ paddingLeft: '5px', fontSize:'13px' }}>
                    Point
                    <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                </span>
            ),
            renderCell: (params) => (
                <TextField
                    fullWidth                   
                    variant="outlined"
                    placeholder='Point Value *'
                    size="small"
                    type="number"
                    inputProps={{
                        step: "any"
                    }}
                    defaultValue={params.row.Point || ""}
                    helperText={showErrorMessage && !params.row.Point ? <span style={{ color: "red", fontSize:'10px' }}>Point value is required</span> : ""}
                    FormHelperTextProps={{ sx: { color: "red" } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            padding: '2px',
                            fontSize: '12px',
                            height: '30px',
                            width: '110px'
                        },
                        '& .MuiInputLabel-root': {
                            fontSize: '12px',
                        },
                        '& .MuiFormHelperText-root': {
                            fontSize: '10px'
                        }
                    }}
                    onBlur={(event) => {
                        if (!event.target.value.trim()) {
                            params.row.Point = "";
                        } else {
                            onEditCell(event, params.row.id, "Point");
                        }
                    }}
                />
            ),
        },
        {
            field: 'Delete',
            headerName: 'Delete',
            width: 100,
            sortable: false,
            renderHeader: () => (
                <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>

                    <IconButton
                        onClick={onDeleteAllRows} title="Delete All"
                        sx={{ display: 'flex', alignItems: 'center', marginLeft: '0px', padding: '4px 8px 4px 0px' }}
                    >
                        <Iconify icon="fluent:delete-32-regular" width='15px' height='15px' color="red" />

                    </IconButton>
                    <span>Delete</span>
                </div>
            ),           
            renderCell: (params) => (
                <IconButton onClick={(event) => onDeleteRow(event, params.row.id)}>
                    <Iconify icon="fluent:delete-32-regular" width="15px" height="15px" color="red" />
                </IconButton>
            ),        

        },
    ];

    return (
        <div
            style={{
                visibility: rows?.length === 0 ? 'hidden' : 'visible',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <style>
                {`
                    .MuiFormHelperText-root {
                        color: red;
                        font-size: 10px !important;
                    }
                `}
            </style>
            <Divider />
            <StripedDataGrid
                localeText={{ noRowsLabel: '' }}
                rows={rows}
                size='small'
                columns={columns}
                getRowHeight={() => "auto"}
                headerHeight={35}
                pageSize={100}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
                getRowId={(row) => row.id}
                sx={{
                    height: '100%',
                    '& .odd': {
                        backgroundColor: 'background.default',
                    },
                    '& .even': {
                        backgroundColor: 'action.hover',
                    },
                }}
                components={{
                    Pagination: null,
                }}
            />
        </div>
    );
});

InternalGrid.displayName = 'InternalGrid';

InternalGrid.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        Item: PropTypes.string,
        MT: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Point: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })).isRequired,
    onEditCell: PropTypes.func.isRequired,
    onDeleteRow: PropTypes.func.isRequired,
    onDeleteAllRows: PropTypes.func.isRequired,
    showErrorMessage: PropTypes.bool.isRequired,
};




const SchemePointDataGrid = ({ gridData, setGridData, showErrorMessage }) => {
    const [rows, setRows] = useState([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    // Memoized callback to handle cell edits
    const handleEditCell = useCallback((event, id, field) => {
        const updatedValue = event.target.value;
        const finalValue = updatedValue === '' ? '' : updatedValue;

        setGridData(prevGridData =>
            prevGridData.map((item, index) => {
                if (index === id) {
                    return {
                        ...item,
                        [field]: finalValue,
                    };
                }
                return item;
            })
        );
    }, [setGridData]);

    // Memoized callback to handle row deletion
    const handleDeleteRow = useCallback((event, id) => {
        setGridData(prevGridData => {
            const updatedGridData = [...prevGridData];
            updatedGridData.splice(id, 1);

            return updatedGridData.map((item, index) => ({
                ...item,
                id: index,
            }));
        });
    }, [setGridData]);

    const handleDeleteAllRows = useCallback(() => {
        setShowDeleteConfirmation(true);
    }, []);

    const handleConfirmDeleteAll = useCallback((confirm) => {
        if (confirm) {
            setGridData([]);
        }
        setShowDeleteConfirmation(false);
    }, [setGridData]);




 

    useEffect(() => {
        // Update rows when gridData changes
        if (gridData && Array.isArray(gridData)) {
            // Add MT and Point fields with empty values and ensure each row has an id
            
            setRows(gridData);
        }
    }, [gridData]);


    return (
        <div
            style={{
                visibility: gridData?.length === 0 ? 'hidden' : 'visible',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <style>
                {`
        .MuiFormHelperText-root {
            color: red;
            font-size: 10px !important;
        }
    `}
            </style>

        <Divider />
            <InternalGrid
                rows={rows}
                onEditCell={handleEditCell}
                onDeleteRow={handleDeleteRow}
                onDeleteAllRows={handleDeleteAllRows}
                showErrorMessage={showErrorMessage}
            />
            {showDeleteConfirmation && (
                <DeletePopup
                    setDeleteConfirm={setShowDeleteConfirmation}
                    confirm={handleConfirmDeleteAll}
                    title="Confirm Deletion"
                    msg="Are you sure you want to delete all rows?"
                />
            )}
        </div>
    );
};
SchemePointDataGrid.propTypes = {
    gridData: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Item: PropTypes.string,
        MT: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Point: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })).isRequired,
    setGridData: PropTypes.func.isRequired,
    showErrorMessage: PropTypes.bool.isRequired,
};

export default SchemePointDataGrid;
